"use client";

import { config, PAID_PLAN_TIERS } from "@repo/config";
import { useClarityAnalytics, useUmamiAnalytics } from "@analytics";
import { LocaleLink } from "@i18n/routing";
import { useSession } from "@saas/auth/hooks/use-session";
import { useCredits } from "@saas/credits/hooks/use-credits";
import { usePurchases } from "@saas/payments/hooks/purchases";
import { NavBadge } from "@saas/shared/components/NavBadge";
import { UserMenu } from "@saas/shared/components/UserMenu";
import type { NavItem } from "@saas/shared/config/nav-items";
import { reloadIfSamePage, useNavItems } from "@saas/shared/config/nav-items";
import type { Locale } from "@repo/i18n";
import { LocaleSwitch } from "@shared/components/LocaleSwitch";
import { Logo } from "@shared/components/Logo";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@ui/components/tooltip";
import { cn } from "@ui/lib";
import { ChevronRightIcon, CrownIcon, LanguagesIcon, Zap } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";

export function AppSidebar() {
	const t = useTranslations("saas.sidebar");
	const locale = useLocale() as Locale;
	const { user } = useSession();
	const { getRemainingCredits, getTotalCredits, hasUnlimitedCredits } =
		useCredits();
	const { hasSubscription } = usePurchases();
	const isPaidPlan = hasSubscription([...PAID_PLAN_TIERS]);
	const { trackEvent: trackUmamiEvent } = useUmamiAnalytics();
	const { trackEvent: trackClarityEvent } = useClarityAnalytics();

	// The collapsed badge is icon-only, so a text-matching click rule can never
	// see it — hence tracking the intent in code instead.
	const trackUpgradeClick = (location: string) => {
		void trackUmamiEvent("upgrade-clicked", { location });
		trackClarityEvent("upgrade-clicked");
	};

	const {
		main: sidebarItems,
		creation: creationItems,
		models: modelItems,
	} = useNavItems();

	// 图标条只有图标，靠 tooltip 认字、sr-only 给读屏；完整模式文字常显，tooltip 隐藏。
	// 不用 aria-label：它会盖掉完整模式下可见的文字
	const SidebarItem = ({ item }: { item: NavItem }) => (
		<>
			<Tooltip delayDuration={0}>
				<TooltipTrigger asChild>
					<LocaleLink
						href={item.href}
						// 照 DeeVid：高 34、圆角 16、图标 18；选中 6% 品牌色底，没选中近黑字（10/19）。间距和内边距比 DeeVid 各少 2px，216 宽里 Poppins 的 pt-BR「Imagem para Vídeo」（139px）才不折行
						className={cn(
							"flex items-center justify-center gap-3 rounded-2xl px-2 py-[7px] transition-colors sidebar-full:justify-start sidebar-full:px-2.5",
							item.active
								? "bg-primary/[0.06] text-primary font-medium"
								: "text-foreground/80 hover:bg-black/[0.04] hover:text-foreground",
						)}
					>
						<item.Icon className="size-[18px] shrink-0" />
						<span className="sr-only sidebar-full:hidden">
							{item.title}
						</span>
						<div className="hidden sidebar-full:flex items-center gap-2 flex-1 min-w-0">
							<span className="text-sm">{item.title}</span>
							{item.badge ? (
								<NavBadge label={item.badge} />
							) : null}
							{item.children && (
								// 提示悬停有子菜单（10/19）。触屏时子项已直接列在下面，不加
								<ChevronRightIcon
									aria-hidden
									className="ml-auto size-3.5 shrink-0 opacity-50 [@media(pointer:coarse)]:hidden"
								/>
							)}
						</div>
					</LocaleLink>
				</TooltipTrigger>
				{item.children ? (
					// 有子项时两种模式都弹：Radix Tooltip 默认可悬停进内容，鼠标移过去能点
					<TooltipContent
						side="right"
						align="start"
						// 弹层挂在 body 上，studio-theme 要再挂一次才是 Poppins + 中性灰
						className="studio-theme flex min-w-48 flex-col gap-0.5 rounded-xl p-1.5"
					>
						{item.children.map((child) => (
							<LocaleLink
								key={child.href}
								href={child.href}
								onClick={reloadIfSamePage}
								aria-current={child.active ? "page" : undefined}
								className={cn(
									"flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm transition-colors",
									child.active
										? "bg-primary/[0.06] text-primary font-medium"
										: "text-foreground/80 hover:bg-black/[0.04] hover:text-foreground",
								)}
							>
								<child.Icon className="h-4 w-4 shrink-0" />
								{child.title}
								{child.badge ? (
									<NavBadge label={child.badge} />
								) : null}
							</LocaleLink>
						))}
					</TooltipContent>
				) : (
					<TooltipContent
						side="right"
						className="sidebar-full:hidden"
					>
						{item.title}
					</TooltipContent>
				)}
			</Tooltip>
			{item.children && (
				// 触屏没有悬停，Tooltip 弹不出来（触屏平板恒为完整模式）：子项像手机抽屉一样缩进列在父项下面
				<div className="hidden [@media(pointer:coarse)]:flex flex-col gap-0.5">
					{item.children.map((child) => (
						<LocaleLink
							key={child.href}
							href={child.href}
							onClick={reloadIfSamePage}
							aria-current={child.active ? "page" : undefined}
							className={cn(
								"flex items-center gap-2.5 rounded-2xl py-1.5 pr-3 pl-10 text-sm transition-colors",
								child.active
									? "bg-primary/[0.06] text-primary font-medium"
									: "text-foreground/80 hover:bg-black/[0.04] hover:text-foreground",
							)}
						>
							<child.Icon className="h-4 w-4 shrink-0" />
							<span className="truncate">{child.title}</span>
							{child.badge ? (
								<NavBadge label={child.badge} />
							) : null}
						</LocaleLink>
					))}
				</div>
			)}
		</>
	);

	return (
		<TooltipProvider>
			{/* 不给手动折叠：收起会把文字、积分数字一起藏掉，侧栏的导流作用就没了。
			    自动切：md–lg 的鼠标设备只留图标（70px）；lg 起或触屏平板带文字（216px，
			    最长的 pt-BR「Imagem para Vídeo」也放得下；10/18 去掉 GPT Image 2 / New 角标后从 240 收窄），
			    条件见 globals.css 的 sidebar-full。
			    右边分隔线照 DeeVid 用 4% 黑（10/19；原来的 border/15 落在白底上看不见） */}
			<div
				className="studio-theme hidden md:flex w-[70px] sidebar-full:w-[216px] flex-col border-r border-black/[0.04] dark:border-border/10 bg-card sticky"
				style={{
					top: "var(--language-banner-height, 0px)",
					height: "calc(100vh - var(--language-banner-height, 0px))",
				}}
			>
				{/* Header */}
				<div className="flex h-16 items-center justify-center p-4 sidebar-full:justify-start">
					<LocaleLink
						href="/"
						className="flex items-center gap-2 overflow-hidden whitespace-nowrap"
					>
						<Logo
							withLabel={false}
							size="sm"
							className="sidebar-full:hidden"
						/>
						<Logo size="sm" className="hidden sidebar-full:flex" />
					</LocaleLink>
				</div>

				{/* Scrollable Content */}
				<div className="flex-1 overflow-y-auto py-4 px-3 space-y-6">
					{/* Account & Navigation Section */}
					<div className="space-y-1">
						{user && (
							<div className="px-1 -mt-2">
								<Tooltip delayDuration={0}>
									<TooltipTrigger asChild>
										<LocaleLink
											href="/subscription"
											onClick={() => {
												// Paid users land here to check their
												// balance, not to upgrade — only free
												// users clicking this are upgrade intent.
												if (!isPaidPlan) {
													trackUpgradeClick(
														"sidebar-credits-badge",
													);
												}
											}}
											className="flex items-center justify-center gap-2 group transition-all duration-300 rounded-lg py-1.5 px-3 border border-transparent sidebar-full:justify-start sidebar-full:bg-accent/20 sidebar-full:hover:bg-accent/40 sidebar-full:hover:border-border/30"
										>
											<div className="relative shrink-0 flex items-center justify-center">
												<Zap className="h-3.5 w-3.5 text-primary/60 fill-primary/10 group-hover:scale-110 transition-transform duration-300" />
												<div className="absolute inset-0 bg-primary/20 blur-sm rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
											</div>

											<span className="sr-only sidebar-full:hidden">
												{t("creditsInfo")}
											</span>

											<div className="hidden sidebar-full:flex items-center justify-between flex-1 gap-2 overflow-hidden">
												<span className="text-[10px] font-bold text-muted-foreground/50 uppercase tracking-[0.15em] whitespace-nowrap">
													{t("energy")}
												</span>
												<span className="text-[11px] font-bold text-foreground/60 tabular-nums">
													{hasUnlimitedCredits(
														"IMAGE_GENERATION",
													)
														? "∞"
														: getRemainingCredits(
																"IMAGE_GENERATION",
															)}
												</span>
											</div>
										</LocaleLink>
									</TooltipTrigger>
									<TooltipContent
										side="right"
										className="flex flex-col gap-1 items-center py-2 px-3 sidebar-full:hidden"
									>
										<span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
											{t("creditsInfo")}
										</span>
										<span className="text-sm font-bold">
											{hasUnlimitedCredits(
												"IMAGE_GENERATION",
											)
												? t("unlimited")
												: getRemainingCredits(
														"IMAGE_GENERATION",
													)}
										</span>
									</TooltipContent>
								</Tooltip>
							</div>
						)}

						{user && (
							<div className="hidden sidebar-full:block h-px bg-border/50 my-2 mx-2" />
						)}

						<div className="space-y-1">
							{sidebarItems.map((item) => (
								<SidebarItem key={item.href} item={item} />
							))}
						</div>
					</div>

					{/* Creation Section */}
					<nav aria-label={t("creation")} className="space-y-1">
						<div className="hidden sidebar-full:block px-2.5 text-xs text-[#9f9f9f] mb-2">
							{t("creation")}
						</div>
						<div className="sidebar-full:hidden h-px bg-border mx-2 my-2" />
						{creationItems.map((item) => (
							<SidebarItem key={item.href} item={item} />
						))}
					</nav>

					{/* Models：照 SuperMaker 单列一组模型页 */}
					<nav aria-label={t("models")} className="space-y-1">
						<div className="hidden sidebar-full:block px-2.5 text-xs text-[#9f9f9f] mb-2">
							{t("models")}
						</div>
						<div className="sidebar-full:hidden h-px bg-border mx-2 my-2" />
						{modelItems.map((item) => (
							<SidebarItem key={item.href} item={item} />
						))}
					</nav>
				</div>

				{/* Bottom Section */}
				<div className="p-3 space-y-2 border-t border-border/15 dark:border-border/10">
					{/* Upgrade Badge */}
					{!isPaidPlan && (
						<>
							<Tooltip delayDuration={0}>
								<TooltipTrigger asChild>
									<LocaleLink
										href="/subscription"
										onClick={() =>
											trackUpgradeClick(
												"sidebar-collapsed",
											)
										}
										aria-label={t("upgradeToPro")}
										className="flex sidebar-full:hidden items-center justify-center p-2 rounded-md bg-gradient-to-br from-amber-100 to-amber-200 dark:from-amber-900/40 dark:to-amber-800/40 border border-amber-200 dark:border-amber-800 text-amber-600 dark:text-amber-100 hover:shadow-md transition-all duration-300 group"
									>
										<CrownIcon className="h-5 w-5 shrink-0 fill-amber-500/20 group-hover:scale-110 transition-transform" />
									</LocaleLink>
								</TooltipTrigger>
								<TooltipContent side="right">
									{t("upgradeToPro")}
								</TooltipContent>
							</Tooltip>
							<LocaleLink
								href="/subscription"
								onClick={() => trackUpgradeClick("sidebar")}
								className="hidden sidebar-full:flex items-center gap-3 px-3 py-3 rounded-lg bg-gradient-to-br from-amber-100 via-amber-200 to-amber-100 dark:from-amber-900/40 dark:via-amber-800/40 dark:to-amber-900/40 border border-amber-200 dark:border-amber-800 text-amber-900 dark:text-amber-100 shadow-sm hover:shadow-md hover:border-amber-300 transition-all duration-300 group"
							>
								<div className="bg-white/50 dark:bg-amber-900/40 p-1.5 rounded-md shrink-0 ring-1 ring-amber-300/30">
									<CrownIcon className="h-4 w-4 shrink-0 fill-amber-500" />
								</div>
								<div className="flex-1 overflow-hidden">
									<div className="text-[13px] font-bold truncate leading-snug">
										{t("upgrade")}
									</div>
									<div className="text-[11px] font-medium opacity-80 line-clamp-2 leading-tight">
										{t("getProFeatures")}
									</div>
								</div>
							</LocaleLink>
						</>
					)}

					{/* 语言切换照 DeeVid 放侧栏底部、升级卡片下面（10/21 老公定；10/20 页脚那个删了，工作台页没别的入口）。
					    完整模式写当前语言名，不认识 Language 这个词也能找到 */}
					{config.i18n.enabled && (
						<LocaleSwitch
							contentProps={{
								side: "right",
								align: "end",
								sideOffset: 18,
							}}
							trigger={
								<button
									type="button"
									aria-label="Language"
									className="flex w-full items-center justify-center gap-3 rounded-2xl px-2 py-[7px] text-foreground/80 transition-colors hover:bg-black/[0.04] hover:text-foreground sidebar-full:justify-start sidebar-full:px-2.5"
								>
									<LanguagesIcon className="size-[18px] shrink-0" />
									<span className="hidden sidebar-full:flex items-center gap-2 flex-1 min-w-0">
										<span className="text-sm truncate">
											{config.i18n.locales[locale]?.label}
										</span>
										<ChevronRightIcon
											aria-hidden
											className="ml-auto size-3.5 shrink-0 opacity-50"
										/>
									</span>
								</button>
							}
						/>
					)}

					{/* User Menu */}
					<div className="pt-1">
						<div className="flex justify-center sidebar-full:hidden">
							<UserMenu
								side="right"
								align="end"
								sideOffset={18}
								className="transition-all duration-200"
							/>
						</div>
						<div className="hidden sidebar-full:block">
							<UserMenu
								showUserName
								side="right"
								align="end"
								sideOffset={18}
								className="transition-all duration-200 border border-transparent hover:border-border hover:bg-accent/50"
							/>
						</div>
					</div>
				</div>
			</div>
		</TooltipProvider>
	);
}
